import { useCallback, useEffect, useRef, useState } from 'react';
import { ASK_RINO_CONFIG } from 'src/constants/AskRinoConstants';

interface AskRinoState {
  isInstalled: boolean;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  installUrl: string;
}

function isExtensionPresent(): boolean {
  return document.querySelector('meta[name="ask-rino-extension"]') !== null;
}

function clickTriggerButton(): boolean {
  const host = document.querySelector('ask-rino-trigger');
  if (host?.shadowRoot) {
    const trigger = host.shadowRoot.querySelector('.trigger-button') as HTMLElement | null;
    if (trigger) {
      trigger.click();
      return true;
    }
  }
  return false;
}

export const useAskRino = (): AskRinoState => {
  const [isInstalled, setIsInstalled] = useState(isExtensionPresent);
  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(false);

  useEffect(() => {
    setIsInstalled(isExtensionPresent());

    const observer = new MutationObserver(() => {
      setIsInstalled(isExtensionPresent());
    });
    observer.observe(document.head, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const open = useCallback(() => {
    if (!isInstalled) {
      window.open(ASK_RINO_CONFIG.installUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (isOpenRef.current) return;
    if (!clickTriggerButton()) {
      window.postMessage({ type: 'ASK_RINO_OPEN' }, '*');
    }
    isOpenRef.current = true;
    setIsOpen(true);
  }, [isInstalled]);

  const close = useCallback(() => {
    if (!isInstalled || !isOpenRef.current) return;
    if (!clickTriggerButton()) {
      // Extension handles ASK_RINO_OPEN as a toggle (no dedicated close message supported)
      window.postMessage({ type: 'ASK_RINO_OPEN' }, '*');
    }
    isOpenRef.current = false;
    setIsOpen(false);
  }, [isInstalled]);

  return { isInstalled, isOpen, open, close, installUrl: ASK_RINO_CONFIG.installUrl };
};
